import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { coreService } from '../services/core.service';
import type { CoreData } from '../types/core-data';

interface CoreDataContextType {
  coreData: CoreData | null;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  load: () => Promise<void>;
  refresh: () => Promise<void>;
}

const CoreDataContext = createContext<CoreDataContextType | undefined>(undefined);

export function CoreDataProvider({ children }: { children: React.ReactNode }) {
  const [coreData, setCoreData] = useState<CoreData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false); // Use ref to prevent duplicate loads even in StrictMode

  const load = useCallback(async () => {
    // Prevent duplicate loads using ref (works even with React StrictMode)
    if (loadingRef.current) {
      console.log('[CoreDataContext] Load already in progress, skipping...');
      return;
    }
    
    console.log('[CoreDataContext] Loading core data...');
    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      // Load core data only (OpenAPI schema not needed)
      const coreDataResult = await coreService.getCoreData();

      setCoreData(coreDataResult);
      console.log('[CoreDataContext] Core data loaded successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load core data';
      console.error('[CoreDataContext] Failed to load core data:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, []); // No dependencies - stable function reference

  const refresh = useCallback(async () => {
    console.log('[CoreDataContext] Refreshing core data...');
    setCoreData(null);
    await load();
  }, [load]);

  const value: CoreDataContextType = {
    coreData,
    isLoaded: !!coreData,
    isLoading,
    error,
    load,
    refresh,
  };

  return <CoreDataContext.Provider value={value}>{children}</CoreDataContext.Provider>;
}

export function useCoreData() {
  const context = useContext(CoreDataContext);
  if (context === undefined) {
    throw new Error('useCoreData must be used within a CoreDataProvider');
  }
  return context;
}

